import React, { useRef, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  CheckCircle,
  Fingerprint,
  Send,
  Description,
  Person
} from '@mui/icons-material';
import SignaturePad from 'signature_pad';
import api from '../lib/api';
import { useQuery, useMutation } from '@tanstack/react-query';

const SignaturePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const [error, setError] = useState<string>('');
  const [activeStep, setActiveStep] = useState(0);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Fetch agreement details
  const { data: agreement, isLoading: agreementLoading } = useQuery({
    queryKey: ['agreement', id],
    queryFn: async () => {
      const res = await api.get(`/agreements/${id}`);
      return res.data.data;
    },
    enabled: !!id
  });

  // Fetch existing signatures
  const { data: signatures, isLoading: signaturesLoading } = useQuery({
    queryKey: ['signatures', id],
    queryFn: async () => {
      const res = await api.get(`/agreements/${id}/signatures`);
      return res.data.data;
    },
    enabled: !!id
  });

  // Signature mutation
  const signMutation = useMutation({
    mutationFn: async (signatureData: string) => {
      const res = await api.post(`/agreements/${id}/signatures`, {
        signatureData,
        signerRole: 'CLIENT'
      });
      return res.data;
    },
    onSuccess: () => {
      navigate(`/agreements/${id}`);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error?.message || 'Failed to submit signature');
    }
  });

  // Initialize signature pad
  useEffect(() => {
    if (canvasRef.current) {
      signaturePadRef.current = new SignaturePad(canvasRef.current, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)',
        minWidth: 1,
        maxWidth: 3
      });

      // Make canvas responsive
      const resizeCanvas = () => {
        const canvas = canvasRef.current;
        if (canvas && signaturePadRef.current) {
          const ratio = Math.max(window.devicePixelRatio || 1, 1);
          canvas.width = canvas.offsetWidth * ratio;
          canvas.height = canvas.offsetHeight * ratio;
          canvas.getContext('2d')?.scale(ratio, ratio);
          signaturePadRef.current.clear();
        }
      };

      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);

      return () => {
        window.removeEventListener('resize', resizeCanvas);
      };
    }
  }, []);

  const handleClear = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  const handleNext = () => {
    if (activeStep === 1) {
      // Moving to signature step - check canvas
      if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
        setError('Please provide your signature');
        return;
      }
    }
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSign = () => {
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
      setError('Please provide your signature');
      return;
    }

    const signatureData = signaturePadRef.current.toDataURL('image/png');
    signMutation.mutate(signatureData);
  };

  if (agreementLoading || signaturesLoading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const steps = ['Review', 'Sign', 'Complete'];

  return (
    <Container maxWidth="lg">
      <Box mt={4} mb={4}>
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <Typography variant="h4" gutterBottom>
            Sign Agreement
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Agreement {agreement?.agreementNumber}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Stepper */}
        <Box mb={4}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Step Content */}
        {activeStep === 0 && (
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 4 }}>
            <Box display="flex" alignItems="center" mb={3}>
              <Description color="primary" sx={{ fontSize: 40, mr: 2 }} />
              <Box>
                <Typography variant="h5">Review Agreement</Typography>
                <Typography variant="body2" color="text.secondary">
                  Read through the details before signing
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Agreement Details */}
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Type</Typography>
                <Typography variant="body1" fontWeight="bold">{agreement?.type}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Components</Typography>
                <Typography variant="body1" fontWeight="bold">{agreement?.totalAssets}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Typography variant="body1" fontWeight="bold">{agreement?.status}</Typography>
              </Box>
              {agreement?.customRequirements && (
                <Box mt={2}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Custom Requirements
                  </Typography>
                  <Typography variant="body2">{agreement.customRequirements}</Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Agreement Terms */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the terms and conditions of this trust agreement
                </Typography>
              }
            />

            <Box display="flex" justifyContent="flex-end" mt={4}>
              <Button variant="contained" onClick={handleNext} disabled={!agreedToTerms}>
                Continue to Sign
              </Button>
            </Box>
          </Paper>
        )}

        {activeStep === 1 && (
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 4 }}>
            <Box display="flex" alignItems="center" mb={3}>
              <Fingerprint color="primary" sx={{ fontSize: 40, mr: 2 }} />
              <Box>
                <Typography variant="h5">Sign Here</Typography>
                <Typography variant="body2" color="text.secondary">
                  Draw your signature in the box below
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Signature Canvas */}
            <Box
              sx={{
                border: '2px solid',
                borderColor: 'primary.main',
                borderRadius: 2,
                p: 3,
                bgcolor: '#fafafa'
              }}
            >
              <canvas
                ref={canvasRef}
                style={{
                  width: '100%',
                  height: '200px',
                  cursor: 'crosshair',
                  display: 'block'
                }}
              />
            </Box>

            <Box display="flex" gap={2} justifyContent="space-between" mt={3}>
              <Button variant="text" onClick={handleClear}>
                Clear
              </Button>
              <Box display="flex" gap={2}>
                <Button variant="outlined" onClick={handleBack}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={signMutation.isPending}
                >
                  Continue
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        {activeStep === 2 && (
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 4 }}>
            <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
              <CheckCircle color="primary" sx={{ fontSize: 80, mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Ready to Sign
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Review your signature and confirm to complete
              </Typography>

              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 3,
                  bgcolor: '#fafafa',
                  width: '100%',
                  maxWidth: '400px'
                }}
                mt={3}
              >
                {signaturePadRef.current && !signaturePadRef.current.isEmpty() && (
                  <img
                    src={signaturePadRef.current.toDataURL('image/png')}
                    alt="Your signature"
                    style={{ maxWidth: '100%', height: 'auto' }}
                  />
                )}
              </Box>

              <Box display="flex" gap={2} mt={4} width="100%" maxWidth="400px">
                <Button variant="outlined" onClick={handleBack} fullWidth>
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSign}
                  disabled={signMutation.isPending}
                  fullWidth
                  startIcon={<Send />}
                >
                  {signMutation.isPending ? 'Signing...' : 'Complete Signing'}
                </Button>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default SignaturePage;

